import type { ScenarioSkyboxStyleString } from './scenarioTypes';

/** Dimension presets for different model types */
export const ModelDimensions = {
  /** FLUX 1.1 Pro Ultra specific dimensions */
  fluxProUltra: [
    { width: 3136, height: 1344, label: '3136x1344 (21:9)' },
    { width: 2752, height: 1536, label: '2752x1536 (16:9)' },
    { width: 2496, height: 1664, label: '2496x1664 (3:2)' },
    { width: 2368, height: 1792, label: '2368x1792 (4:3)' },
    { width: 2304, height: 1856, label: '2304x1856 (5:4)' },
    { width: 2048, height: 2048, label: '2048x2048 (1:1)' },
    { width: 1856, height: 2304, label: '1856x2304 (4:5)' },
    { width: 1792, height: 2368, label: '1792x2368 (3:4)' },
    { width: 1664, height: 2496, label: '1664x2496 (2:3)' },
    { width: 1536, height: 2752, label: '1536x2752 (9:16)' },
    { width: 1344, height: 3136, label: '1344x3136 (9:21)' },
  ],

  /** GPT-Image models */
  gptImage: [
    { width: 1536, height: 1024, label: '1536x1024 (3:2)' },
    { width: 1024, height: 1024, label: '1024x1024 (1:1)' },
    { width: 1024, height: 1536, label: '1024x1536 (2:3)' },
  ],

  /** Standard FLUX/SDXL dimensions */
  standard: [
    { width: 1344, height: 768, label: '1344x768 (16:9)' },
    { width: 1216, height: 832, label: '1216x832 (3:2)' },
    { width: 1104, height: 832, label: '1104x832 (4:3)' },
    { width: 1088, height: 896, label: '1088x896 (5:4)' },
    { width: 1024, height: 1024, label: '1024x1024 (1:1)' },
    { width: 896, height: 1088, label: '896x1088 (4:5)' },
    { width: 832, height: 1104, label: '832x1104 (3:4)' },
    { width: 832, height: 1216, label: '832x1216 (2:3)' },
    { width: 768, height: 1344, label: '768x1344 (9:16)' },
  ],

  /** SD 1.5 dimensions (half of standard) */
  sd15: [
    { width: 672, height: 384, label: '672x384 (16:9)' },
    { width: 608, height: 416, label: '608x416 (3:2)' },
    { width: 552, height: 416, label: '552x416 (4:3)' },
    { width: 544, height: 448, label: '544x448 (5:4)' },
    { width: 512, height: 512, label: '512x512 (1:1)' },
    { width: 448, height: 544, label: '448x544 (4:5)' },
    { width: 416, height: 552, label: '416x552 (3:4)' },
    { width: 416, height: 608, label: '416x608 (2:3)' },
    { width: 384, height: 672, label: '384x672 (9:16)' },
  ],
} as const;

/** Skybox style options organized by category (matching Scenario's UI) */
export const SkyboxStyleCategories = [
  {
    category: 'Realistic & Photographic',
    styles: [
      {
        value: 'photo' as ScenarioSkyboxStyleString,
        label: 'Photo',
        description: 'Photorealistic environments',
        image: 'https://app.scenario.com/skybox-styles/realistic-photo.jpg',
      },
      {
        value: 'cinematic' as ScenarioSkyboxStyleString,
        label: 'Cinematic',
        description: 'Movie-like dramatic lighting',
        image: 'https://app.scenario.com/skybox-styles/realistic-cinematic.jpg',
      },
      {
        value: 'cloud-skydome' as ScenarioSkyboxStyleString,
        label: 'Cloud Skydome',
        description: 'Cloud-focused sky environments',
        image: 'https://app.scenario.com/skybox-styles/realistic-skydome.jpg',
      },
      {
        value: 'space' as ScenarioSkyboxStyleString,
        label: 'Space',
        description: 'Outer space environments',
        image: 'https://app.scenario.com/skybox-styles/realistic-space.jpg',
      },
    ],
  },
  {
    category: 'Traditional Art',
    styles: [
      {
        value: 'oil-painting' as ScenarioSkyboxStyleString,
        label: 'Oil Painting',
        description: 'Classic oil painting',
        image: 'https://app.scenario.com/skybox-styles/traditional-oil-painting.jpg',
      },
      {
        value: 'pastel' as ScenarioSkyboxStyleString,
        label: 'Pastel',
        description: 'Soft pastel colors',
        image: 'https://app.scenario.com/skybox-styles/traditional-pastel.jpg',
      },
      {
        value: 'ink' as ScenarioSkyboxStyleString,
        label: 'Ink',
        description: 'Ink drawing style',
        image: 'https://app.scenario.com/skybox-styles/traditional-pen-ink.jpg',
      },
      {
        value: 'claymation' as ScenarioSkyboxStyleString,
        label: 'Claymation',
        description: 'Clay animation style',
        image: 'https://app.scenario.com/skybox-styles/traditional-claymation.jpg',
      },
    ],
  },
  {
    category: 'Stylized & Illustration',
    styles: [
      {
        value: 'comic' as ScenarioSkyboxStyleString,
        label: 'Comic',
        description: 'Comic book art style',
        image: 'https://app.scenario.com/skybox-styles/stylized-comic.jpg',
      },
      {
        value: 'cartoon' as ScenarioSkyboxStyleString,
        label: 'Cartoon',
        description: 'Western cartoon style',
        image: 'https://app.scenario.com/skybox-styles/stylized-cartoon.jpg',
      },
      {
        value: 'manga' as ScenarioSkyboxStyleString,
        label: 'Manga',
        description: 'Japanese manga style',
        image: 'https://app.scenario.com/skybox-styles/stylized-manga.jpg',
      },
      {
        value: 'manga-color' as ScenarioSkyboxStyleString,
        label: 'Manga Color',
        description: 'Colorized manga style',
        image: 'https://app.scenario.com/skybox-styles/stylized-manga-color.jpg',
      },
      {
        value: 'retro-fantasy' as ScenarioSkyboxStyleString,
        label: 'Retro Fantasy',
        description: 'Retro-styled fantasy',
        image: 'https://app.scenario.com/skybox-styles/stylized-retro-fantasy.jpg',
      },
      {
        value: 'whimsical' as ScenarioSkyboxStyleString,
        label: 'Whimsical',
        description: 'Playful whimsical style',
        image: 'https://app.scenario.com/skybox-styles/stylized-whimsical.jpg',
      },
      {
        value: 'psychedelic' as ScenarioSkyboxStyleString,
        label: 'Psychedelic',
        description: 'Colorful psychedelic art',
        image: 'https://app.scenario.com/skybox-styles/stylized-psychedelic.jpg',
      },
    ],
  },
  {
    category: 'SciFi & Fantasy',
    styles: [
      {
        value: 'scifi-concept-art' as ScenarioSkyboxStyleString,
        label: 'Sci-Fi Concept',
        description: 'Science fiction concept art',
        image: 'https://app.scenario.com/skybox-styles/fantasy-scifi.jpg',
      },
      {
        value: 'fantasy' as ScenarioSkyboxStyleString,
        label: 'Fantasy',
        description: 'Magical fantasy worlds',
        image: 'https://app.scenario.com/skybox-styles/fantasy-fantasy.jpg',
      },
      {
        value: 'cyberpunk' as ScenarioSkyboxStyleString,
        label: 'Cyberpunk',
        description: 'Futuristic cyberpunk aesthetic',
        image: 'https://app.scenario.com/skybox-styles/fantasy-cyberpunk.jpg',
      },
      {
        value: 'neon-tron' as ScenarioSkyboxStyleString,
        label: 'Neon/Tron',
        description: 'Neon-lit futuristic style',
        image: 'https://app.scenario.com/skybox-styles/fantasy-neon.jpg',
      },
      {
        value: 'enchanted' as ScenarioSkyboxStyleString,
        label: 'Enchanted',
        description: 'Mystical enchanted environments',
        image: 'https://app.scenario.com/skybox-styles/fantasy-enchanted.jpg',
      },
    ],
  },
  {
    category: 'Digital & 3D',
    styles: [
      {
        value: '3d-cartoon' as ScenarioSkyboxStyleString,
        label: '3D Cartoon',
        description: '3D cartoon rendering',
        image: 'https://app.scenario.com/skybox-styles/digital-3d-cartoon.jpg',
      },
      {
        value: '3d-rendered' as ScenarioSkyboxStyleString,
        label: '3D Rendered',
        description: 'High-quality 3D rendering',
        image: 'https://app.scenario.com/skybox-styles/digital-3d-cartoon.jpg',
      },
      {
        value: 'minimalist' as ScenarioSkyboxStyleString,
        label: 'Minimalist',
        description: 'Clean minimal design',
        image: 'https://app.scenario.com/skybox-styles/digital-3d-cartoon.jpg',
      },
    ],
  },
  {
    category: 'Other',
    styles: [
      {
        value: 'standard' as ScenarioSkyboxStyleString,
        label: 'Standard',
        description: 'Default balanced style',
        image: 'https://app.scenario.com/skybox-styles/realistic-photo.jpg',
      },
      {
        value: 'photography' as ScenarioSkyboxStyleString,
        label: 'Photography',
        description: 'Natural photography style',
        image: 'https://app.scenario.com/skybox-styles/realistic-photo.jpg',
      },
      {
        value: 'anime' as ScenarioSkyboxStyleString,
        label: 'Anime',
        description: 'Japanese anime art style',
        image: 'https://app.scenario.com/skybox-styles/stylized-manga.jpg',
      },
    ],
  },
] as const;

/** Model guidance ranges by type */
export const ModelGuidanceRanges = {
  fluxPro11: { min: 1, max: 10, default: 3.5 },
  standard: { min: 1, max: 20, default: 7.5 },
} as const;

/** Model inference steps ranges by type */
export const ModelStepsRanges = {
  fluxPro11: { min: 1, max: 4, default: 4 },
  standard: { min: 10, max: 50, default: 28 },
} as const;

/** 3D model capability defaults */
export const Model3DCapabilities = {
  hunyuan: {
    supportsMultiView: true,
    maxFaces: 40000,
    stepRange: { min: 10, max: 50, default: 30 },
    guidanceRange: { min: 1, max: 20, default: 5 },
  },
  direct3d: {
    supportsMultiView: false,
    maxFaces: 20000,
    stepRange: { min: 10, max: 50, default: 30 },
    guidanceRange: { min: 1, max: 20, default: 5 },
  },
  standard: {
    supportsMultiView: false,
    maxFaces: 40000,
    stepRange: { min: 10, max: 50, default: 30 },
    guidanceRange: { min: 1, max: 20, default: 5 },
  },
} as const;

/** Default model preferences by generation type - prefers latest featured, then fallback names */
export const DefaultModelPreferences = {
  texture: {
    fallbackNames: ['Realistic Textures 3.0', 'Realistic Textures'],
  },
  image: {
    fallbackNames: ['flux', 'FLUX'],
  },
  model3D: {
    fallbackId: 'model_hunyuan-3d-pro-i23d',
    fallbackNames: ['hunyuan'],
  },
} as const;

/** Capability constants for filtering models */
export const ModelCapabilities = {
  texture: ['img2img_texture', 'txt2img_texture'],
  image: ['txt2img', 'img2img'],
  model3D: ['img23d', 'txt23d'],
  model3DExcluded: ['3d23d'], // not supported yet
  video: ['img2video', 'txt2video', 'video2video'],
  audio: ['txt2audio', 'audio2audio'],
} as const;

/** Capability filter options for model selectors (with human-readable labels) */
export const ModelCapabilityFilters = {
  texture: [
    { value: 'img2img_texture', label: 'Image to Texture' },
    { value: 'txt2img_texture', label: 'Text to Texture' },
  ],
  image: [
    { value: 'txt2img', label: 'Text to Image' },
    { value: 'img2img', label: 'Image to Image' },
  ],
  model3D: [
    { value: 'img23d', label: 'Image to 3D' },
    { value: 'txt23d', label: 'Text to 3D' },
    { value: '3d23d', label: '3D to 3D' },
  ],
} as const;

